import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface CountCardProps {
  icon: LucideIcon;
  label: string;
  count: number;
  loading: boolean;
  /** Pet/customer names to display as avatar list — top 5, "+N more" if overflow. */
  names?: string[];
  className?: string;
}

export function CountCard({
  icon: Icon,
  label,
  count,
  loading,
  names = [],
  className,
}: CountCardProps) {
  const TOP_N = 5;
  const visible = names.slice(0, TOP_N);
  const overflow = names.length - TOP_N;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-border bg-surface px-5 py-4',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="size-4" strokeWidth={1.75} />
        <span className="text-[10.5px] font-medium uppercase tracking-[0.14em]">{label}</span>
      </div>

      {loading ? (
        <>
          <div className="h-8 w-12 animate-pulse rounded-md bg-secondary" />
          <div className="h-3.5 w-32 animate-pulse rounded bg-secondary" />
        </>
      ) : (
        <>
          <span className="num text-3xl font-semibold tabular-nums leading-none">{count}</span>
          {visible.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {visible.join(', ')}
              {overflow > 0 && (
                <span className="num ml-1 text-muted-foreground/70">+{overflow} more</span>
              )}
            </p>
          )}
          {visible.length === 0 && count === 0 && (
            <p className="text-xs text-muted-foreground">None today</p>
          )}
        </>
      )}
    </div>
  );
}
